import { QUESTION_TYPES } from '../api/constants';
import { getQuestionSpecificPrompt } from './questionPrompts';
import { formatEventStats, groupMetrics, formatMetric } from './statsFormatter';

/**
 * Formats event data for prompt generation
 */
export const formatEventData = (data) => {
  // console.log('📊 Formatting event data:', {
  //   inputData: JSON.stringify(data, null, 2),
  //   timestamp: new Date().toISOString()
  // });

  // Add null check for data
  if (!data) {
    console.warn('❌ No data provided to formatEventData');
    return 'No data available for analysis.';
  }

  const { eventDetails, posts = [], stats } = data;
  
  // Add more detailed logging for stats
  // console.log('📊 EVENT STATS DETAILS:', {
  //   hasStats: !!stats,
  //   statsType: typeof stats,
  //   statsObj: stats,
  //   statsKeys: stats ? Object.keys(stats) : [],
  //   hasSummary: !!stats?.summary,
  //   summaryKeys: stats?.summary ? Object.keys(stats.summary) : [],
  //   summaryData: stats?.summary,
  //   hasMetrics: !!stats?.metrics,
  //   metricsKeys: stats?.metrics ? Object.keys(stats.metrics) : [],
  //   metricsData: stats?.metrics,
  //   timestamp: new Date().toISOString()
  // });
  
  // Add null check for eventDetails
  if (!eventDetails) {
    console.warn('❌ No event details provided');
    return 'Event details not available for analysis.';
  }
  
  // console.log('📝 Processing event details:', {
  //   hasEventDetails: !!eventDetails,
  //   eventType: eventDetails?.question_type,
  //   eventName: eventDetails?.name,
  //   eventDetails: JSON.stringify(eventDetails, null, 2)
  // });

  // console.log('📝 Processing posts:', {
  //   postsLength: posts.length,
  //   postsType: typeof posts,
  //   isPostsArray: Array.isArray(posts),
  //   postsSample: posts.slice(0, 2) ? JSON.stringify(posts.slice(0, 2), null, 2) : null
  // });

  // console.log('📝 Processing stats:', {
  //   hasStats: !!stats,
  //   statsType: typeof stats,
  //   hasSummary: !!stats?.summary,
  //   statsSummary: stats?.summary ? JSON.stringify(stats.summary, null, 2) : null
  // });

  // Format posts into readable text
  let formattedPosts = '';
  try {
    // Handle both direct array and object with posts property
    const postsArray = Array.isArray(posts) ? posts : posts?.posts;
    
    if (Array.isArray(postsArray)) {
      // console.log('📝 Processing posts array:', {
      //   postsLength: postsArray.length,
      //   firstPost: postsArray[0],
      //   timestamp: new Date().toISOString()
      // });
      
      formattedPosts = postsArray.map(post => {
        // console.log('📝 Processing post:', {
        //   hasPost: !!post,
        //   postType: typeof post,
        //   hasContent: !!post?.content,
        //   hasChildren: !!post?.children,
        //   childrenType: typeof post?.children,
        //   isChildrenArray: Array.isArray(post?.children),
        //   timestamp: new Date().toISOString()
        // });

        const content = post?.content || '';
        const comments = Array.isArray(post?.children) 
          ? post.children.map(child => `- ${child?.content || ''}`).join('\n')
          : '';
        return `${content}\n${comments}`;
      }).join('\n\n');
    } else {
      console.warn('📝 Posts is not an array:', {
        postsType: typeof posts,
        postsValue: posts,
        postsArrayType: typeof postsArray,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('❌ Error formatting posts:', {
      error: error.message,
      errorType: error.name,
      postsType: typeof posts,
      postsValue: posts,
      timestamp: new Date().toISOString()
    });
    formattedPosts = '';
  }

  // Format stats into readable text
  // console.log('📊 Processing stats for formatting:', {
  //   hasStats: !!stats,
  //   statsType: typeof stats,
  //   hasMetrics: !!stats?.metrics,
  //   hasSummary: !!stats?.summary,
  //   metrics: stats?.metrics ? JSON.stringify(stats.metrics, null, 2) : null,
  //   summary: stats?.summary ? JSON.stringify(stats.summary, null, 2) : null,
  //   timestamp: new Date().toISOString()
  // });

  // Format stats with defensive coding
  let formattedStats = '';
  if (stats) {
    try {
      // Create a stats section even if we only have an empty object
      formattedStats = `
    Total Responses: ${stats.metrics?.contributed || 0}
    Participation Rate: ${stats.metrics?.total_user ? Math.round((stats.metrics.contributed / stats.metrics.total_user) * 100) : 0}%
    ${stats.summary?.simple_score ? `Average Score: ${stats.summary.simple_score}
    Score Range: ${stats.summary.min_score || 1} - ${stats.summary.max_score || 5}` : ''}
    `;
    } catch (error) {
      console.error('❌ Error formatting stats:', {
        error: error.message,
        statsObj: stats,
        timestamp: new Date().toISOString()
      });
      // Provide basic stats info even if there's an error
      formattedStats = 'Statistics data available but could not be formatted properly.';
    }
  } else {
    formattedStats = 'No statistics available for this event.';
  }

  // console.log('📊 Formatted stats result:', {
  //   rawStats: stats,
  //   totalResponses: stats?.metrics?.contributed || 0,
  //   participationRate: stats?.metrics?.total_user ? Math.round((stats.metrics.contributed / stats.metrics.total_user) * 100) : 0,
  //   averageScore: stats?.summary?.simple_score,
  //   minScore: stats?.summary?.min_score,
  //   maxScore: stats?.summary?.max_score,
  //   formattedStats,
  //   timestamp: new Date().toISOString()
  // });

  const result = `
Event Details:
- Name: ${eventDetails?.name || 'N/A'}
- Question Type: ${eventDetails?.question_type || 'N/A'}
- Description: ${eventDetails?.description || 'N/A'}

Responses:
${formattedPosts}

Statistics:
${formattedStats}
  `;

  // console.log('✅ Formatted event data:', {
  //   result: result,
  //   timestamp: new Date().toISOString()
  // });

  return result;
};

/**
 * Generates appropriate prompt based on question type
 */
export const getPromptForQuestionType = (questionType, promptData) => {
  // console.log('🔍 getPromptForQuestionType called with:', {
  //   questionType,
  //   hasPromptData: !!promptData,
  //   promptDataKeys: promptData ? Object.keys(promptData) : [],
  //   hasEventDetails: !!promptData?.eventDetails,
  //   hasPostsData: !!promptData?.posts,
  //   postsLength: promptData?.posts?.length,
  //   hasStats: !!promptData?.stats,
  //   timestamp: new Date().toISOString()
  // });

  // Validate inputs
  if (!questionType) {
    console.error('❌ Missing questionType in getPromptForQuestionType');
    return null;
  }

  if (!promptData) {
    console.error('❌ Missing promptData in getPromptForQuestionType');
    return null;
  }

  const { eventDetails, posts, stats } = promptData;
  
  // Add more detailed logging for stats
  // console.log('📊 EVENT STATS IN PROMPT GENERATION:', {
  //   hasStats: !!stats,
  //   statsType: typeof stats,
  //   statsKeys: stats ? Object.keys(stats) : [],
  //   hasSummary: !!stats?.summary,
  //   summaryData: stats?.summary ? JSON.stringify(stats.summary) : null,
  //   hasMetrics: !!stats?.metrics,
  //   metricsData: stats?.metrics ? JSON.stringify(stats.metrics) : null,
  //   questionType,
  //   timestamp: new Date().toISOString()
  // });
  
  // Validate required data
  if (!eventDetails) {
    console.error('❌ Missing eventDetails in promptData:', {
      questionType,
      promptDataKeys: Object.keys(promptData),
      timestamp: new Date().toISOString()
    });
    return null;
  }

  try {
    // Detailed logging of data structure
    // console.log('📊 Data structure in getPromptForQuestionType:', {
    //   eventDetails: {
    //     hasEventDetails: !!eventDetails,
    //     eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
    //     name: eventDetails?.name,
    //     title: eventDetails?.title,
    //     question_type: eventDetails?.question_type,
    //     session_type: eventDetails?.session_type
    //   },
    //   posts: {
    //     hasPostsArray: !!posts,
    //     isPostsArray: Array.isArray(posts),
    //     postsLength: posts?.length,
    //     firstPostSample: posts && posts.length > 0 ? {
    //       hasContent: !!posts[0]?.content,
    //       contentLength: posts[0]?.content?.length,
    //       hasChildren: !!posts[0]?.children,
    //       childrenLength: posts[0]?.children?.length
    //     } : null
    //   },
    //   stats: {
    //     hasStats: !!stats,
    //     statsKeys: stats ? Object.keys(stats) : [],
    //     hasMetrics: !!stats?.metrics,
    //     hasParticipation: !!stats?.participation,
    //     hasResponseRate: !!stats?.response_rate
    //   },
    //   timestamp: new Date().toISOString()
    // });

    // Log formatted data for debugging
    const formattedDataForLogging = formatEventData({
      eventDetails,
      posts,
      stats
    });
    
    // console.log('📝 Formatted data for prompt:', {
    //   hasFormattedData: !!formattedDataForLogging,
    //   formattedDataLength: formattedDataForLogging?.length,
    //   timestamp: new Date().toISOString()
    // });

    // Get question-specific prompt - pass the structured data objects
    const prompt = getQuestionSpecificPrompt(questionType, { eventDetails, posts, stats });
    
    // console.log('🤖 Generated prompt result:', {
    //   hasPrompt: !!prompt,
    //   promptLength: prompt?.length,
    //   promptPreview: prompt ? prompt.substring(0, 100) + '...' : null,
    //   timestamp: new Date().toISOString()
    // });

    return prompt;
  } catch (error) {
    console.error('❌ Error in getPromptForQuestionType:', {
      error: error.message,
      stack: error.stack,
      questionType,
      timestamp: new Date().toISOString()
    });
    return null;
  }
};

const generateBinaryQuestionPrompt = (question, responses, eventStats) => {
  // console.log('📊 generateBinaryQuestionPrompt stats:', {
  //   hasEventStats: !!eventStats,
  //   statsType: typeof eventStats, 
  //   statsKeys: eventStats ? Object.keys(eventStats) : [],
  //   hasSummary: !!eventStats?.summary,
  //   hasMetrics: !!eventStats?.metrics,
  //   timestamp: new Date().toISOString()
  // });

  // Safely format stats, handling empty data
  const formattedStats = eventStats ? formatEventStats(eventStats) : { summary: {}, metrics: {} };
  const groupedMetrics = formattedStats.metrics ? groupMetrics(formattedStats.metrics) : { 
    userTypes: {}, 
    platformUsage: {}, 
    engagement: {}, 
    interactions: {}, 
    sentiment: {} 
  };

  // Calculate participation rate with null checks
  const totalUsers = formattedStats.metrics?.total_user || 0;
  const contributedUsers = formattedStats.metrics?.contributed || 0;
  const participationRate = totalUsers > 0 ? (contributedUsers / totalUsers) * 100 : 0;

  // Calculate engagement metrics with null checks
  const totalActivities = formattedStats.metrics?.total_activities || 0;
  const totalViews = formattedStats.metrics?.views || 0;
  const uniqueViewers = formattedStats.metrics?.viewed_user || 0;

  return `You are an expert data analyst analyzing a Binary Choice survey. This type of survey presents respondents with exactly two options (typically Yes/No or Agree/Disagree). Analyze the following data and provide a structured report in markdown format:

## Event Analysis Data

Event Details:
- Name: ${question}
- Question Type: binary
- Description: N/A

Responses:
${responses.length > 0 
  ? responses.map((r, i) => r).join('\n')
  : 'No responses available'}

Statistics:
${formattedStats.metrics && Object.keys(formattedStats.metrics).length > 0 ? `
- Total Responses: ${contributedUsers}
- Total Views: ${totalViews}
- Unique Viewers: ${uniqueViewers}
- Total Activities: ${totalActivities}
- Participation Rate: ${participationRate.toFixed(1)}%
` : ''}
  

## Analysis Instructions
1. Summarize the binary choice responses into a clear executive summary
2. Identify key themes and patterns in the responses
3. Calculate the distribution of yes/no or agree/disagree responses
4. Analyze any correlation between user types and response patterns
5. Note any significant engagement metrics or participation rates

## Report Format
Please format your response as follows:

# Executive Summary
[1-2 paragraph summary of the overall findings]

# Analysis
- [Bullet points with key analytical insights]
- [Include distribution percentages where relevant]
- [Include participation and engagement insights]

# Key Themes
- [List 3-5 major themes identified in the responses]

# Recommendations
- [Provide 3-5 actionable next steps based on the analysis]`;
};

const generateMultipleChoicePrompt = (question, responses, eventStats) => {
  const formattedStats = formatEventStats(eventStats);
  const groupedMetrics = groupMetrics(formattedStats.metrics || {});

  // Calculate participation rate
  const totalUsers = formattedStats.metrics?.total_user || 0;
  const contributedUsers = formattedStats.metrics?.contributed || 0;
  const participationRate = totalUsers > 0 ? (contributedUsers / totalUsers) * 100 : 0;

  // Calculate engagement metrics
  const totalActivities = formattedStats.metrics?.total_activities || 0;
  const totalViews = formattedStats.metrics?.views || 0;
  const uniqueViewers = formattedStats.metrics?.viewed_user || 0;

  return `Analyze the following multiple choice question responses and provide insights:

## Survey Context
- Question: ${question}
- Total Responses: ${formattedStats.metrics?.contributed || 0}
- Total Views: ${totalViews}
- Unique Viewers: ${uniqueViewers}
- Total Activities: ${totalActivities}
- Participation Rate: ${participationRate.toFixed(1)}%

## User Engagement Analysis
${Object.entries(groupedMetrics.userTypes || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Platform Distribution
${Object.entries(groupedMetrics.platformUsage || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Response Analysis
${responses.length > 0 
  ? `Responses:\n${responses.map((r, i) => `${i + 1}. ${r}`).join('\n')}`
  : 'No responses available'}

## Key Insights
- Analyze the distribution of responses across options
- Identify any patterns in option selection
- Consider the participation rate and engagement levels
- Note any correlation between user types and option preferences

## Actionable Recommendations
- Suggest 3-5 concrete actions based on the responses
- Prioritize recommendations based on impact and feasibility
- Include specific suggestions for improving future engagement`;
};

const generateScalePrompt = (question, responses, eventStats) => {
  const formattedStats = formatEventStats(eventStats);
  const groupedMetrics = groupMetrics(formattedStats.metrics || {});

  // Calculate participation rate
  const totalUsers = formattedStats.metrics?.total_user || 0;
  const contributedUsers = formattedStats.metrics?.contributed || 0;
  const participationRate = totalUsers > 0 ? (contributedUsers / totalUsers) * 100 : 0;

  // Calculate engagement metrics
  const totalActivities = formattedStats.metrics?.total_activities || 0;
  const totalViews = formattedStats.metrics?.views || 0;
  const uniqueViewers = formattedStats.metrics?.viewed_user || 0;

  // Get score range from summary
  const minScore = formattedStats.summary?.min_score || 0;
  const maxScore = formattedStats.summary?.max_score || 0;
  const averageScore = formattedStats.summary?.simple_score || 0;

  return `Analyze the following scale question responses and provide insights:

## Survey Context
- Question: ${question}
- Total Responses: ${formattedStats.metrics?.contributed || 0}
- Total Views: ${totalViews}
- Unique Viewers: ${uniqueViewers}
- Total Activities: ${totalActivities}
- Participation Rate: ${participationRate.toFixed(1)}%
- Score Range: ${minScore} - ${maxScore}
- Average Score: ${averageScore}

## User Engagement Analysis
${Object.entries(groupedMetrics.userTypes || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Platform Distribution
${Object.entries(groupedMetrics.platformUsage || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Response Analysis
${responses.length > 0 
  ? `Responses:\n${responses.map((r, i) => `${i + 1}. ${r}`).join('\n')}`
  : 'No responses available'}

## Key Insights
- Analyze the distribution of responses across the scale
- Calculate and interpret the average rating
- Consider the participation rate and engagement levels
- Note any correlation between user types and rating patterns

## Actionable Recommendations
- Suggest 3-5 concrete actions based on the responses
- Prioritize recommendations based on impact and feasibility
- Include specific suggestions for improving future engagement`;
};

const generateStackRankPrompt = (question, responses, eventStats) => {
  const formattedStats = formatEventStats(eventStats);
  const groupedMetrics = groupMetrics(formattedStats.metrics || {});

  // Calculate participation rate
  const totalUsers = formattedStats.metrics?.total_user || 0;
  const contributedUsers = formattedStats.metrics?.contributed || 0;
  const participationRate = totalUsers > 0 ? (contributedUsers / totalUsers) * 100 : 0;

  // Calculate engagement metrics
  const totalActivities = formattedStats.metrics?.total_activities || 0;
  const totalViews = formattedStats.metrics?.views || 0;
  const uniqueViewers = formattedStats.metrics?.viewed_user || 0;

  return `Analyze the following stack rank question responses and provide insights:

## Survey Context
- Question: ${question}
- Total Responses: ${formattedStats.metrics?.contributed || 0}
- Total Views: ${totalViews}
- Unique Viewers: ${uniqueViewers}
- Total Activities: ${totalActivities}
- Participation Rate: ${participationRate.toFixed(1)}%

## User Engagement Analysis
${Object.entries(groupedMetrics.userTypes || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Platform Distribution
${Object.entries(groupedMetrics.platformUsage || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Response Analysis
${responses.length > 0 
  ? `Responses:\n${responses.map((r, i) => `${i + 1}. ${r}`).join('\n')}`
  : 'No responses available'}

## Key Insights
- Analyze the ranking patterns and preferences
- Identify any consensus or divergence in rankings
- Consider the participation rate and engagement levels
- Note any correlation between user types and ranking patterns

## Actionable Recommendations
- Suggest 3-5 concrete actions based on the responses
- Prioritize recommendations based on impact and feasibility
- Include specific suggestions for improving future engagement`;
};

const generateOpenEndedPrompt = (question, responses, eventStats) => {
  const formattedStats = formatEventStats(eventStats);
  const groupedMetrics = groupMetrics(formattedStats.metrics || {});

  // Calculate participation rate
  const totalUsers = formattedStats.metrics?.total_user || 0;
  const contributedUsers = formattedStats.metrics?.contributed || 0;
  const participationRate = totalUsers > 0 ? (contributedUsers / totalUsers) * 100 : 0;

  // Calculate engagement metrics
  const totalActivities = formattedStats.metrics?.total_activities || 0;
  const totalViews = formattedStats.metrics?.views || 0;
  const uniqueViewers = formattedStats.metrics?.viewed_user || 0;

  // Calculate sentiment percentages
  const positiveSentiment = formattedStats.metrics?.posts_sentiment_positive || 0;
  const neutralSentiment = formattedStats.metrics?.posts_sentiment_neutral || 0;
  const negativeSentiment = formattedStats.metrics?.posts_sentiment_negative || 0;
  const totalSentiment = positiveSentiment + neutralSentiment + negativeSentiment;

  return `Analyze the following open-ended survey responses and provide insights:

## Survey Context
- Question: ${question}
- Total Responses: ${formattedStats.metrics?.contributed || 0}
- Total Views: ${totalViews}
- Unique Viewers: ${uniqueViewers}
- Total Activities: ${totalActivities}
- Participation Rate: ${participationRate.toFixed(1)}%

## User Engagement Analysis
${Object.entries(groupedMetrics.userTypes || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Platform Distribution
${Object.entries(groupedMetrics.platformUsage || {})
  .map(([key, value]) => formatMetric(key, value))
  .join('\n')}

## Response Analysis
${responses.length > 0 
  ? `Responses:\n${responses.map((r, i) => `${i + 1}. ${r}`).join('\n')}`
  : 'No responses available'}

## Sentiment Analysis
- Positive: ${positiveSentiment} (${totalSentiment > 0 ? Math.round((positiveSentiment/totalSentiment)*100) : 0}%)
- Neutral: ${neutralSentiment} (${totalSentiment > 0 ? Math.round((neutralSentiment/totalSentiment)*100) : 0}%)
- Negative: ${negativeSentiment} (${totalSentiment > 0 ? Math.round((negativeSentiment/totalSentiment)*100) : 0}%)

## Key Insights
- Identify 3-5 main themes or patterns in the responses
- Highlight any surprising or unexpected findings
- Note any areas of consensus or disagreement
- Consider the sentiment distribution and its implications

## Actionable Recommendations
- Suggest 3-5 concrete actions based on the responses
- Prioritize recommendations based on impact and feasibility
- Include specific suggestions for improving future engagement`;
}; 